
import React from "react";
import { type ExampleType } from "./Example";
import ExampleBlock from "./ExampleBlock";
import ExampleHeader from "./ExampleHeader";

export type ExamplesType = Record<string, Array<Record<string, ExampleType>>>;

type Props = {
    examples: ExamplesType;
};

type State = {
    languages: Array<string>;
    language: null | string;
};

export default class DocumentationExamples extends React.Component<Props, State> {
    public state: State = {
        languages: [],
        language : null,
    };

    public updateLanguages = (): void => {
        const { examples } = this.props;

        // Get current language
        let language: string | null = localStorage.getItem("example-language");

        if(!language) {
            language = this.state.language;
        }

        // Prepare to get languages
        const languageMap: Record<string, boolean> = {};

        // Loop through sections
        for(const section in examples) {
            if(!Object.prototype.hasOwnProperty.call(examples, section)) {
                continue;
            }

            // Get language examples in sections
            let exampleLanguages = examples[section];

            if(!Array.isArray(exampleLanguages)) {
                exampleLanguages = [ exampleLanguages ];
            }

            for(let i = 0, l = exampleLanguages.length; i < l; i++) {
                for(const language in exampleLanguages[i]) {
                    if(!Object.prototype.hasOwnProperty.call(exampleLanguages[i], language)) {
                        continue;
                    }

                    languageMap[language] = true;
                }
            }
        }

        // Check to see if examples language should be reset
        let setLanguage = false;

        if(!language || !languageMap[language]) {
            setLanguage = true;
        }

        // Convert results to array
        const languages = Object.keys(languageMap);

        // Set language
        if(setLanguage) {
            language = languages[0];
        }

        this.setState({
            languages,
            language,
        });
    };

    public componentDidMount(): void {
        this.updateLanguages();
    }

    public componentDidUpdate(prevProps: Readonly<Props>): void {
        if(prevProps.examples !== this.props.examples) {
            this.updateLanguages();
        }
    }

    public onLanguageChange = (language: string): void => {
        localStorage.setItem("example-language", language);

        this.setState({
            language,
        });
    };

    public render(): React.ReactNode {
        const { examples } = this.props;
        const { languages, language } = this.state;

        const sections = Object.keys(examples);

        if(!sections.length) {
            return null;
        }

        return (
            <React.Fragment>
                <ExampleHeader
                    languages={languages}
                    language={language}
                    onChange={this.onLanguageChange}
                />
                {sections.map(section => {
                    let exp = examples[section];

                    if(!Array.isArray(exp)) {
                        exp = [ exp ];
                    }

                    return (
                        <ExampleBlock
                            key={section + "|" + language}
                            section={section}
                            language={language as string}
                            examples={exp}
                        />
                    );
                })}
            </React.Fragment>
        );
    }
}
